/**
 * Canonical Excel Export Hook — APV ERP Standard
 * Encapsulates:
 * - Server query with active filters / search preservation
 * - Selected rows export (respects user table selections)
 * - Client-side local data export fallback
 * - Loading toast & execution state
 * - Clean error notifications
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { exportToExcel, type ExcelColumnDef, type ExportToExcelOptions } from '@/lib/excel.utils';

export interface UseExcelExportOptions<T = any> {
  /** Base filename without extension (e.g. 'danh_sach_khach_hang') */
  filename: string;
  /** Column definitions conforming to ExcelColumnDef */
  columns: ExcelColumnDef<T>[];
  /** Name of the Excel sheet (default: 'Dữ Liệu') */
  sheetName?: string;
  /** Server-side query function (e.g. trpcClient.customers.list.query) */
  queryFn?: (params: any) => Promise<{ items?: T[]; data?: T[] } | T[] | any>;
  /** Legacy alias for queryFn */
  serverQuery?: (params: any) => Promise<{ items?: T[]; data?: T[] } | T[] | any>;
  /** Current active filters to merge when querying server (search, status, dateRange, etc.) */
  currentFilters?: Record<string, any>;
  /** Legacy alias for currentFilters */
  filterValues?: Record<string, any>;
  /** Optional transformation applied to raw server items before exporting */
  transformData?: (items: any[]) => T[];
  /** Client-side local data array (used when no server queryFn is provided) */
  localData?: T[];
  /** Set or Array of selected IDs to export ONLY selected rows if any are chosen */
  selectedIds?: Set<string> | string[];
  /** Key to match ID when filtering selectedIds (default: 'id') */
  idKey?: string;
  /** Max items to fetch from server (default: 5000) */
  limit?: number;
  /** Callback triggered before export starts */
  onBeforeExport?: () => void;
  /** Callback triggered after export completes successfully */
  onSuccess?: (count: number) => void;
  /** Custom notification message */
  successMessage?: (count: number) => string;
}

export function useExcelExport<T = any>({
  filename,
  columns,
  sheetName = 'Dữ Liệu',
  queryFn,
  serverQuery,
  currentFilters = {},
  filterValues = {},
  transformData,
  localData,
  selectedIds,
  idKey = 'id',
  limit = 5000,
  onBeforeExport,
  onSuccess,
  successMessage,
}: UseExcelExportOptions<T>) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    if (onBeforeExport) {
      onBeforeExport();
    }

    setIsExporting(true);
    const toastId = toast.loading('Đang tải toàn bộ dữ liệu từ máy chủ để xuất Excel...');

    try {
      let itemsToExport: T[] = [];
      const activeQueryFn = queryFn || serverQuery;

      // Determine selected IDs set if present
      const selectedSet = selectedIds
        ? selectedIds instanceof Set
          ? selectedIds
          : new Set(selectedIds)
        : null;
      const hasSelection = Boolean(selectedSet && selectedSet.size > 0);

      // ── PRIORITY 1: Server Query provided (Always fetch ALL matching records, ignoring client page size) ──
      if (activeQueryFn) {
        const mergedFilters: Record<string, any> = {
          ...(filterValues || {}),
          ...(currentFilters || {}),
        };

        const cleanFilters: Record<string, any> = {};
        Object.entries(mergedFilters).forEach(([k, v]) => {
          if (k === 'page' || k === 'limit' || k === 'pageSize' || k.startsWith('_')) return;
          if (v === undefined || v === null || v === '' || v === 'ALL' || v === 'all') return;

          if (Array.isArray(v)) {
            const list = v.filter(Boolean);
            if (list.length === 1) cleanFilters[k] = list[0];
            else if (list.length > 1) cleanFilters[k] = list;
          } else if (typeof v === 'object' && !(v instanceof Date)) {
            const { from, to } = v as { from?: string; to?: string };
            if (from) cleanFilters[`${k}From`] = from;
            if (to) cleanFilters[`${k}To`] = to;
          } else {
            cleanFilters[k] = v;
          }
        });

        const queryParams = {
          ...cleanFilters,
          page: 1,
          limit,
        };

        const response = await activeQueryFn(queryParams);

        let fetchedItems: any[] = [];
        if (Array.isArray(response)) {
          fetchedItems = response;
        } else if (response && typeof response === 'object') {
          if (Array.isArray(response.items)) {
            fetchedItems = response.items;
          } else if (Array.isArray(response.data)) {
            fetchedItems = response.data;
          }
        }

        const processedItems: T[] = transformData
          ? transformData(fetchedItems)
          : (fetchedItems as T[]);

        if (hasSelection && selectedSet) {
          itemsToExport = processedItems.filter((item: any) => {
            const itemId = item?.[idKey] ?? item?.id ?? item?._id;
            return selectedSet.has(itemId);
          });
        } else {
          itemsToExport = processedItems;
        }
      }
      // ── PRIORITY 2: Client-side Local Data fallback (only when no server query exists) ──
      else if (localData && Array.isArray(localData)) {
        const baseLocal = transformData ? transformData(localData) : [...localData];
        if (hasSelection && selectedSet) {
          itemsToExport = baseLocal.filter((item: any) => {
            const itemId = item?.[idKey] ?? item?.id ?? item?._id;
            return selectedSet.has(itemId);
          });
        } else {
          itemsToExport = baseLocal;
        }
      }

      // Check if data is empty
      if (!itemsToExport || itemsToExport.length === 0) {
        toast.dismiss(toastId);
        toast.warning('Không có dữ liệu phù hợp để xuất Excel.');
        return;
      }

      // Perform Excel generation
      const options: ExportToExcelOptions = {
        sheetName,
      };

      await exportToExcel(itemsToExport, columns, filename, options);

      const msg = successMessage
        ? successMessage(itemsToExport.length)
        : `Đã xuất ${itemsToExport.length} dòng ra file Excel thành công!`;

      toast.success(msg, { id: toastId });

      if (onSuccess) {
        onSuccess(itemsToExport.length);
      }
    } catch (err: any) {
      console.error('[useExcelExport] Error during export:', err);
      toast.error(`Xuất Excel thất bại: ${err?.message || 'Lỗi không xác định'}`, {
        id: toastId,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    filename,
    columns,
    sheetName,
    queryFn,
    serverQuery,
    currentFilters,
    filterValues,
    transformData,
    localData,
    selectedIds,
    idKey,
    limit,
    onBeforeExport,
    onSuccess,
    successMessage,
  ]);

  return {
    handleExport,
    exportToExcel: handleExport,
    isExporting,
  };
}
